import React, { useCallback, useMemo } from 'react'
import { actionTypes, getDefaultExecutionHour } from 'consts/automatedActionConsts'
import SendMessage from './SendMessage'
import { Grid, makeStyles } from '@material-ui/core'
import AddPatientTag from './AddPatientTag'
import ShowAnnouncement from './ShowAnnouncement'
import AssignPatient from './AssignPatient'
import NotifyPractice from './NotifyPractice'
import RuleExecutionHourField from '../RuleExecutionHourField'

const useStyles = makeStyles(theme => ({
  container: {
    marginLeft: 45
  }
}))

const AutomationRulesActionEditor = ({
  actionType,
  actions,
  setActions,
  errors,
  isTimeBased = false,
  isNewRule = false,
  isSystem
}) => {
  const classes = useStyles()

  const actionParams = useMemo(() => actions?.[0]?.params || null, [actions])

  const handleActionChange = useCallback(
    ({ type, params }) => {
      setActions([
        {
          type,
          // executionHour is owned by this editor's shared RuleExecutionHourField (a sibling of
          // the action component), not by the action component itself - every leaf action
          // (AddPatientTag/AssignPatient/NotifyPractice/ShowAnnouncement) rebuilds its own
          // `params` from scratch and has no reason to know about it. So it can't be trusted to
          // come through in the action's own payload and must be carried forward explicitly here.
          // The incoming payload still wins if it does include one (e.g. SendMessage, which
          // already merges it in itself). Only carried forward while the trigger is still
          // time-based - otherwise a stale hour from a since-abandoned time-based trigger would
          // ride along on an unrelated action edit and get persisted on confirm.
          params: {
            ...(isTimeBased && actionParams?.executionHour ? { executionHour: actionParams.executionHour } : {}),
            ...params
          }
        }
      ])
    },
    [setActions, actionParams, isTimeBased]
  )

  const handleExecutionHourChange = useCallback(
    executionHour => {
      // Functional update that only ever touches the hour: it must not rewrite `type` (the modal
      // owns keeping it in sync with `actionType`), and rebuilding params from render-time
      // actionParams would race the mount-effect of the action's own onChange on a brand new rule.
      setActions(prev => [
        {
          ...(prev?.[0] || { type: actionType }),
          params: { ...(prev?.[0]?.params || {}), executionHour }
        }
      ])
    },
    [setActions, actionType]
  )

  const actionsComponents = useMemo(
    () => ({
      [actionTypes.AddPatientTag]: AddPatientTag,
      [actionTypes.SendMessage]: SendMessage,
      [actionTypes.AssignPatient]: AssignPatient,
      [actionTypes.ShowAnnouncement]: ShowAnnouncement,
      [actionTypes.NotifyPractice]: NotifyPractice
    }),
    []
  )

  return (
    <Grid container alignItems="center" className={classes.container}>
      {actionsComponents[actionType] && (
        <Grid item xs={12}>
          {React.createElement(actionsComponents[actionType], {
            onChange: handleActionChange,
            params: actionParams,
            errors,
            isTimeBased,
            isSystem
          })}
        </Grid>
      )}
      {isTimeBased && actionsComponents[actionType] && (
        <Grid item xs={12} style={{ marginTop: 10 }}>
          <RuleExecutionHourField
            value={actionParams?.executionHour}
            onChange={handleExecutionHourChange}
            defaultHour={getDefaultExecutionHour({ isNewRule })}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default AutomationRulesActionEditor
